// Package imports:
import React, { useState } from 'react';

// UI element imports:
import Button from '../../../ui-elements/Button/Button';
import Modal from '../../../ui-elements/Modal/Modal';

// Component imports:
import ColorPickerField from './ColorPickerField';
import ImageUploadField, { ImagePreview } from './ImageUploadField';

interface ICustomPageSettingsProps {
    slug: string;
    setSlug: (slug: string) => void;
    primaryColor: string;
    setPrimaryColor: (color: string) => void;
    secondaryColor: string;
    setSecondaryColor: (color: string) => void;
    accentColor: string;
    setAccentColor: (color: string) => void;
    // Custom logo
    customLogoPreview: ImagePreview | null;
    setCustomLogoPreview: (preview: ImagePreview | null) => void;
    customLogoError: string;
    setCustomLogoError: (error: string) => void;
    setCustomLogoFile: (file: File | null) => void;
    // Background
    backgroundPreview: ImagePreview | null;
    setBackgroundPreview: (preview: ImagePreview | null) => void;
    backgroundError: string;
    setBackgroundError: (error: string) => void;
    setBackgroundFile: (file: File | null) => void;
    // Reset
    onReset: () => void;
}

const CustomPageSettings: React.FC<ICustomPageSettingsProps> = ({
    slug,
    setSlug,
    primaryColor,
    setPrimaryColor,
    secondaryColor,
    setSecondaryColor,
    accentColor,
    setAccentColor,
    customLogoPreview,
    setCustomLogoPreview,
    customLogoError,
    setCustomLogoError,
    setCustomLogoFile,
    backgroundPreview,
    setBackgroundPreview,
    backgroundError,
    setBackgroundError,
    setBackgroundFile,
    onReset,
}) => {
    const [showResetModal, setShowResetModal] = useState(false);

    return (
        <div className="kyc-settings__custom-page-section">
            <div className="custom-page__section-header">
                <h4 className="kyc-settings__section-title">Sérsniðin síða</h4>
                <Button
                    buttonType="secondary"
                    size="sm"
                    onClick={() => setShowResetModal(true)}
                >
                    Endurstilla
                </Button>
            </div>
            <p className="kyc-settings__section-text">
                Hér er hægt sérsníða vefinn með lógói, litum og bakgrunnsmynd fyrirtækisins - þannig birtist hann viðskiptavinum þínum þegar þeir svara áreiðanleikakönnun.
            </p>

            <div className="custom-page__config-layout">
                {/* Left: controls */}
                <div className="custom-page__config-left">
                    {/* Slug */}
                    <div className="custom-page__slug-field">
                        <label className="custom-page__slug-label">Veldu slóð</label>
                        <div className="custom-page__slug-input-wrapper">
                            <span className="custom-page__slug-prefix">kyc.keldan.is/</span>
                            <input
                                type="text"
                                className="custom-page__slug-input"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                placeholder="mitt-fyrirtaeki"
                                maxLength={50}
                                spellCheck={false}
                            />
                        </div>
                    </div>

                    {/* Colors */}
                    <div className="custom-page__colors">
                        <label className="custom-page__colors-label">Litaval</label>
                        <p className="kyc-settings__section-text">
                            Veldu liti sem passa við útlit fyrirtækisins þíns. Litir birtast á sérsniðinni síðunni.
                        </p>
                        <div className="custom-page__color-pickers">
                            <ColorPickerField
                                label="Aðallitur"
                                value={primaryColor}
                                onChange={setPrimaryColor}
                            />
                            <ColorPickerField
                                label="Aukalitur (bakgrunnur)"
                                value={secondaryColor}
                                onChange={setSecondaryColor}
                            />
                            <ColorPickerField
                                label="Áherslulitur (takkar o.fl.)"
                                value={accentColor}
                                onChange={setAccentColor}
                            />
                        </div>
                    </div>
                </div>

                {/* Right: preview */}
                <div className="custom-page__config-right">
                    <div className="custom-page__preview">
                        <label className="custom-page__preview-label">Forskoðun</label>
                        <div
                            className="custom-page__preview-card"
                            style={{
                                background: backgroundPreview
                                    ? `url(${backgroundPreview.url}) center/cover no-repeat`
                                    : (secondaryColor || '#f5f5f5'),
                            }}
                        >
                            <div
                                className="custom-page__preview-header"
                                style={{ backgroundColor: primaryColor || '#003366' }}
                            >
                                {customLogoPreview && (
                                    <img
                                        src={customLogoPreview.url}
                                        alt="Lógó"
                                        className="custom-page__preview-logo"
                                    />
                                )}
                                {!customLogoPreview && (
                                    <span className="custom-page__preview-logo-placeholder">Lógó/fyrirtæki</span>
                                )}
                            </div>
                            <div className="custom-page__preview-body">
                                <div className="custom-page__preview-content">
                                    {/* Mock question area with secondary color */}
                                    <div
                                        className="custom-page__preview-questions"
                                        style={{ backgroundColor: secondaryColor || '#f5f5f5' }}
                                    >
                                        <div className="custom-page__preview-q">
                                            <span className="custom-page__preview-q-label">1. Spurning</span>
                                            <div className="custom-page__preview-q-input" />
                                        </div>
                                        <div className="custom-page__preview-q">
                                            <span className="custom-page__preview-q-label">2. Spurning</span>
                                            <div className="custom-page__preview-q-radios">
                                                <label className="custom-page__preview-q-radio">
                                                    <span
                                                        className="custom-page__preview-q-radio-dot custom-page__preview-q-radio-dot--active"
                                                        style={{ borderColor: accentColor || '#0066cc', backgroundColor: accentColor || '#0066cc' }}
                                                    />
                                                    <span>Já</span>
                                                </label>
                                                <label className="custom-page__preview-q-radio">
                                                    <span className="custom-page__preview-q-radio-dot" />
                                                    <span>Nei</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="custom-page__preview-button"
                                        style={{ backgroundColor: accentColor || '#0066cc' }}
                                    >
                                        Senda svör
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image uploads — side by side */}
            <div className="custom-page__uploads-row">
                <ImageUploadField
                    label="Lógó fyrirtækis"
                    description="Lógóið birtist á sérsniðinni síðunni."
                    preview={customLogoPreview}
                    setPreview={setCustomLogoPreview}
                    error={customLogoError}
                    setError={setCustomLogoError}
                    setFile={setCustomLogoFile}
                    maxWidthPx={600}
                    maxHeightPx={300}
                    maxSizeKB={150}
                    previewHeight={40}
                />
                <ImageUploadField
                    label="Bakgrunnsmynd"
                    description="Bakgrunnsmyndin birtist á sérsniðinni síðunni."
                    preview={backgroundPreview}
                    setPreview={setBackgroundPreview}
                    error={backgroundError}
                    setError={setBackgroundError}
                    setFile={setBackgroundFile}
                    maxWidthPx={5000}
                    maxHeightPx={5000}
                    maxSizeKB={2048}
                    previewHeight={80}
                />
            </div>

            {/* Reset confirmation modal */}
            <Modal
                show={showResetModal}
                onHide={() => setShowResetModal(false)}
                hasCloseButton
                header="Endurstilla sérsniðna síðu"
                body={
                    <>
                        <p>
                            Ertu viss um að þú viljir endurstilla allar stillingar fyrir sérsniðnu síðuna þína?
                            Slóð, litir, lógó og bakgrunnsmynd verða fjarlægð.
                        </p>
                        <p>
                            Þú þarft að vista breytingarnar til að þær taki gildi.
                        </p>
                        <div className="modal__button-row">
                            <Button buttonType="secondary" onClick={() => setShowResetModal(false)}>
                                Hætta við
                            </Button>
                            <Button
                                buttonType="primary"
                                onClick={() => {
                                    onReset();
                                    setShowResetModal(false);
                                }}
                            >
                                Endurstilla
                            </Button>
                        </div>
                    </>
                }
            />
        </div>
    );
};

export default CustomPageSettings;
